import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { ActivityModel } from 'app/models'
import { ActivityType } from '@/app/constants/activity'
import { getActivityType } from '@/modules/drivers/helpers/ActivityTypeResolver'
import { formatDuration } from '@/modules/drivers/hooks/useDateHelper'
import {
  faBedFrontRegular,
  faCalendarDaysSolid,
  FaIcon,
  faHammerRegular,
  faIdBadgeRegular,
  faTruckRegular,
} from '@/lib/fa'

type MonthlyChartHeaderProps = {
  startTime: Moment
  days: number
  activities: ReadonlyArray<ActivityModel>
  translate: (key: string) => any
}

export const SUMMARY_TYPES = [
  { type: ActivityType.DRIVING, icon: faTruckRegular },
  { type: ActivityType.WORK, icon: faHammerRegular },
  { type: ActivityType.AVAILABILITY, icon: faIdBadgeRegular },
  { type: ActivityType.REST, icon: faBedFrontRegular },
]

export const calcDurations = (
  start: Moment,
  end: Moment,
  activities: ReadonlyArray<ActivityModel>,
): Record<string, number> => {
  const durations: Record<string, number> = {
    [ActivityType.DRIVING]: 0,
    [ActivityType.WORK]: 0,
    [ActivityType.AVAILABILITY]: 0,
    [ActivityType.REST]: 0,
    [ActivityType.UNKNOWN]: 0,
  }

  activities.forEach((activity) => {
    let st = moment(activity.startTime)
    let et = moment(activity.endTime)

    if (et < start || st > end) return
    if (et.toDate().getFullYear() === 9999 || st.toDate().getFullYear() === 1) {
      return
    }

    if (st < start) st = start.clone()
    if (et > end) et = end.clone()
    if (!st.isValid() || !et.isValid()) return

    const ms = moment.duration(et.diff(st)).asMilliseconds()
    if (!Number.isFinite(ms) || ms < 0) return

    durations[getActivityType(activity.type)] += ms
  })

  return durations
}

const MonthlyChartHeader: React.FC<MonthlyChartHeaderProps> = ({
  startTime,
  days,
  activities,
  translate,
}) => {
  const start = moment(startTime)
  const end = start.clone().add(days, 'days')
  const lastDay = start.clone().add(days - 1, 'days')

  const weekWord = translate('monthlyActivity.week')?.toString?.() || 'Tydzień'
  const firstWeek = start.isoWeek()
  const lastWeek = lastDay.isoWeek()
  const weekLabel =
    firstWeek === lastWeek
      ? `${weekWord} ${firstWeek}`
      : `${weekWord} ${firstWeek}–${lastWeek}`

  const rangeLabel = `od ${start.format('DD-MM')} do ${lastDay.format('DD-MM')}`

  const durations = calcDurations(start, end, activities)

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-bg-white tw-px-4 tw-py-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-text-content-base">
        <FaIcon
          icon={faCalendarDaysSolid}
          className="tw-h-3.5 tw-text-primary"
        />
        <span className="tw-text-sm tw-font-semibold">{weekLabel}</span>
        <span className="tw-text-xs tw-text-content-base/60">·</span>
        <span className="tw-text-xs tw-text-content-base/60">{rangeLabel}</span>
      </div>

      <div className="tw-flex tw-items-center tw-gap-4">
        {SUMMARY_TYPES.map(({ type, icon }) => (
          <div
            key={type}
            className="tw-flex tw-items-center tw-gap-1.5 tw-text-content-base"
          >
            <FaIcon icon={icon} className="tw-h-3.5 tw-text-content-base/70" />
            <span className="tw-text-xs tw-font-medium tw-tabular-nums">
              {formatDuration(moment.duration(durations[type]))}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MonthlyChartHeader
